/* eslint-disable no-nested-ternary */
/* eslint-disable no-restricted-globals */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import { useFlags } from "launchdarkly-react-client-sdk";

import { Accordion, AccordionDetails, AccordionSummary, Divider, Grid, Stack, Tooltip } from "@mui/material";

import PendingRequests from "components/Screens/Workforce/UserDetailsV2/Timeoff/PendingLeaves";
import CustomDropdown from "components/common/Dropdown";
import PageLoader from "components/common/PageLoader";
import HistoryCard from "./HistoryCard";

import {
  getCustomerEmployeeLeaves,
  getEmployeeLeavesDataForCustomer,
  getEmployeePendingLeaves,
} from "services/useTimeoffService";

import combineUpcomingLeavesAndHolidays from "util/combineUpcomingLeavesAndHolidays";
import LeavesValidityDates from "components/Screens/Timeoff/TimeoffAdmin/EmployeeTimeOffDetails/LeavesValidityDates";

import {
  ArrowBack,
  ArrowForward,
  ExpandMore as ExpandMoreIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";

import InfoIcon from "assets/svgs/InfoIcon";
import LeftArrow from "assets/images/left-arrow.svg";
import CalendarIcon from "assets/svgs/CalendarIcon";
import BeachHolidayIcon from "assets/images/beach-holiday.svg?react";

import "./Timeoff.scss";

function TimeoffTab() {
  const navigate = useNavigate();

  const { anniversaryBasedTimeOff } = useFlags();

  const [leaveRequests, setLeavesRequests] = useState({});
  const [historyDropdownLeaves, setHistoryDropdownLeaves] = useState([]);
  const [employeeLeavesData, setEmployeeLeavesData] = useState([]);
  const [historyLeaveType, setHistoryLeaveType] = useState("scheduled");
  const [loading, setLoading] = useState(false);
  const [leavesAndHolidays, setLeavesAndHolidays] = useState([]);
  const [upcomingLeaves, setUpcomingLeaves] = useState([]);
  const [currentShownLeaveCard, setCurrentShownLeaveCard] = useState(0);
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const allHolidays = useSelector((state) => state?.Holidays?.allHolidays);

  const fetchEmployeeLeavesDataForCustomer = async () => {
    const res = await getEmployeeLeavesDataForCustomer(location.pathname.split("/")[3], anniversaryBasedTimeOff);
    setEmployeeLeavesData(res?.payload?.data);
  };

  const fetchEmployeePendingLeaves = async () => {
    const res = await getEmployeePendingLeaves(location.pathname.split("/")?.[3], anniversaryBasedTimeOff);
    setLeavesRequests(res?.payload?.data || {});
  };

  const filteredLeavesData = !anniversaryBasedTimeOff
    ? employeeLeavesData?.filter((leave) => ["Annual", "Sick", "Paternity", "Unpaid"].includes(leave?.leaveType))
    : employeeLeavesData;

  const checkScrollButtons = () => {
    if (scrollContainerRef.current && filteredLeavesData?.length > 5) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    } else {
      setCanScrollLeft(false);
      setCanScrollRight(false);
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current && canScrollLeft) {
      scrollContainerRef.current.scrollBy({
        left: -220,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current && canScrollRight) {
      scrollContainerRef.current.scrollBy({
        left: 220,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await fetchEmployeeLeavesDataForCustomer();
        await fetchEmployeePendingLeaves();
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const updateHistoryDropdown = async () => {
      try {
        setLoading(true);
        const { pending, ...restLeaves } = leaveRequests;
        setHistoryDropdownLeaves(
          Object.keys(restLeaves).map((leave) => ({
            key: leave,
            label: leave,
          }))
        );
      } catch (error) {
        console.error("Failed to update history dropdown:", error);
      } finally {
        setLoading(false);
      }
    };

    if (Object.keys(leaveRequests).length > 0) {
      updateHistoryDropdown();
    }
  }, [leaveRequests]);

  const fetchUpcomingLeavesForEmployee = async () => {
    const res = await getCustomerEmployeeLeaves(location.pathname?.split("/")?.[3], anniversaryBasedTimeOff);
    setUpcomingLeaves(res?.payload?.data);
  };

  useEffect(() => {
    fetchUpcomingLeavesForEmployee();
  }, []);

  useEffect(() => {
    setLeavesAndHolidays(combineUpcomingLeavesAndHolidays(upcomingLeaves, allHolidays?.data));
  }, [upcomingLeaves, allHolidays]);

  useEffect(() => {
    const timer = setTimeout(() => {
      checkScrollButtons();
    }, 100);

    return () => clearTimeout(timer);
  }, [filteredLeavesData?.length]);

  useEffect(() => {
    const handleResize = () => {
      checkScrollButtons();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const slicedLeavesAndHolidays = leavesAndHolidays?.slice(currentShownLeaveCard, currentShownLeaveCard + 2);
  const onHistoryCLick = (leave) =>
    navigate("timeoff", {
      state: {
        leaveData: leaveRequests?.[historyLeaveType],
        currentLeave: leave,
      },
    });

  const showScrollControls = filteredLeavesData?.length > 5;

  return (
    <div>
      {loading && <PageLoader />}
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          mb: "8px"
        }}>
        <p className="heading-h5-semibold">Leaves taken</p>

        {employeeLeavesData?.length && employeeLeavesData?.[0]?.startDate && employeeLeavesData?.[0]?.endDate && (
          <LeavesValidityDates
            startDate={employeeLeavesData?.[0]?.startDate}
            endDate={employeeLeavesData?.[0]?.endDate}
          />
        )}
      </Stack>
      <div
        className={`leave-cards-scroll-wrapper ${showScrollControls && canScrollLeft ? "shadow-left" : ""} ${showScrollControls && canScrollRight ? "shadow-right" : ""}`}
      >
        {showScrollControls && (
          <div className={`scroll-arrow scroll-arrow-left ${!canScrollLeft ? "disabled" : ""}`} onClick={scrollLeft}>
            <ChevronLeftIcon />
          </div>
        )}

        <div
          className={`timeoff-taken-tab ${showScrollControls ? "scrollable" : ""}`}
          ref={scrollContainerRef}
          onScroll={checkScrollButtons}
        >
          {filteredLeavesData?.map((leaveData, index) => {
            const { scheduled = 0, pending = 0, taken, noOfLeaves, leaveType } = leaveData || {};

            const hasScheduledOrPending = scheduled + pending > 0;
            const showTooltip = leaveType === "Unpaid" && !anniversaryBasedTimeOff;

            return (
              <React.Fragment key={leaveType || `${taken}-${noOfLeaves}`}>
                <div className={`leave-count w-25p ${hasScheduledOrPending ? "align-self-center" : ""}`}>
                  <div className="leave-count-flex-row">
                    <div className="heading-h1-semibold color-text-neutral-20">{taken}</div>
                    <div className="leave-count-total">{noOfLeaves > 0 ? `/${noOfLeaves}` : ""}</div>
                  </div>

                  {/* tooltips will be added in phase 2 of timeoff, temporarily commenting this to continue work on this later 
              {anniversaryBasedTimeOff && employeeCountry === "Pakistan" ? (
                <CustomTooltip
                  content={getTooltipContentForCountries("Maternity", "Pakistan", 4)}
                  arrow
                  placement="right-start"
                >
                  <InfoOutlinedIcon className="time-off-tooltip-info-icon" />
                </CustomTooltip>
              ) : null} */}

                  {leaveType && (
                    <div className="leave-type-container">
                      <div className="leave-card-type">{leaveType}</div>
                      {showTooltip && (
                        <Tooltip
                          slotProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: "#292A3D",
                                color: "#F5F5F5",
                                borderRadius: "8px",
                                fontSize: "12px",
                                width: "70%",
                                padding: "9px",
                              },
                            },
                          }}
                          title="For each unpaid leave your employee takes, we will reimburse. Contact accounts for more details"
                          cursor
                          placement="bottom"
                        >
                          <div style={{ cursor: "pointer" }}>
                            <InfoIcon fill="#44146c" size="18" />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  )}

                  {hasScheduledOrPending && (
                    <div className="leave-count-scheduled-pending">{`+${scheduled + pending} scheduled/pending`}</div>
                  )}
                </div>

                {index < filteredLeavesData.length - 1 && <Divider flexItem orientation="vertical" />}
              </React.Fragment>
            );
          })}
        </div>

        {showScrollControls && (
          <div className={`scroll-arrow scroll-arrow-right ${!canScrollRight ? "disabled" : ""}`} onClick={scrollRight}>
            <ChevronRightIcon />
          </div>
        )}
      </div>
      <p className="heading-h5-semibold color-text-headings-primary mt-2 mb-1">Upcoming Leaves and holidays</p>
      <div className="timeoff-taken-tab border-none bg-primary-95 flex gap-1 p-1">
        <ArrowBack
          className={`time-off-arrow-back ${currentShownLeaveCard === 0 ? "time-off-arrow-back--disabled" : "time-off-arrow-back--enabled"}`}
          onClick={() => {
            if (currentShownLeaveCard !== 0) setCurrentShownLeaveCard(currentShownLeaveCard - 1);
          }}
        />
        {slicedLeavesAndHolidays?.map((leave) => (
          <Grid
            className="flex"
            sx={{
              borderRadius: "12px",
              background: "#FFF",
              boxShadow: "0px 1px 6px 0px rgba(2, 0, 110, 0.08), 0px 1px 1px 0px rgba(2, 0, 110, 0.06)",
              width: "45%",
              padding: "0.75rem 0.5rem",
            }}
          >
            <span className="p-1 h-max-con  rounded-lg bg-primary-95 flex">
              {!leave?.isHoliday ? (
                <BeachHolidayIcon />
              ) : (
                <CalendarIcon style={{ transform: "scale(1.5)" }} fill="#44146c" />
              )}
            </span>
            <div className="flex flex-col">
              <div className="pl-1">
                <div className="pending-leave-reason">{leave?.label} </div>

                <div className="pending-leave-duration ">
                  <div className="pending-leave-date">{moment.utc(leave?.startDate).format("MMM DD, YYYY")}</div>
                  {leave?.endDate ? (
                    <>
                      <img className="pending-leave-arrow" src={LeftArrow} alt="left arrow" />
                      <div className="pending-leave-date"> {moment(leave?.endDate).format("MMM DD, YYYY")}</div>
                    </>
                  ) : null}
                </div>
              </div>
              <div className=" pl-1 pt-05 flex items-center">
                <div className="timeoff-used-label"> {leave?.isLeave ? "Total time-off used" : "Total Days off"}</div>
                <div className="timeoff-used-value">
                  {leave?.totalTimeOff} {leave?.totalTimeOff == 1 ? "day" : "days"}
                </div>
              </div>
            </div>
          </Grid>
        ))}

        <ArrowForward
          className={`time-off-arrow-forward ${
            currentShownLeaveCard === leavesAndHolidays?.length - 1 ? "time-off-arrow-forward--disabled" : ""
          }`}
          onClick={() => {
            if (currentShownLeaveCard !== leavesAndHolidays?.length - 1)
              setCurrentShownLeaveCard(currentShownLeaveCard + 1);
          }}
        />
      </div>
      <span style={{ overflowY: "scroll", maxHeight: "73vh", display: "block" }}>
        <div className="mt-15">
          <PendingRequests
            pendingLeaveRequests={leaveRequests?.pending}
            fetchEmployeePendingLeaves={fetchEmployeePendingLeaves}
          />
        </div>

        <div className="mt-15 timeoff-history-container">
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header">
              <p className="para-body-m-medium">History</p>
            </AccordionSummary>
            <AccordionDetails>
              <CustomDropdown
                options={historyDropdownLeaves}
                onSelect={(key) => {
                  setHistoryLeaveType(key);
                }}
                buttonLabel={historyDropdownLeaves.find((leave) => leave.label === historyLeaveType)?.label}
              />
              <div className="mt-1 flex flex-col gap-05">
                {leaveRequests?.[historyLeaveType]?.length ? (
                  <div>
                    {leaveRequests?.[historyLeaveType]?.map((leave) => (
                      <HistoryCard data={leave} onClick={() => onHistoryCLick(leave)} />
                    ))}
                  </div>
                ) : (
                  <p className="text-center">No data available</p>
                )}
              </div>
            </AccordionDetails>
          </Accordion>
        </div>
      </span>
    </div>
  );
}

export default TimeoffTab;
